import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Api from 'util/Api';
import api from 'constants/api';

export const OWNER_TYPE = Object.freeze({
  MARKETPLACE: 40,
  STORE: 20,
});

export type Channel = 'email' | 'whatsapp';

export type BlockType =
  | 'header'
  | 'text'
  | 'button'
  | 'footer'
  | 'image'
  | 'qr';
export type BlockAlign = 'left' | 'center' | 'right';
export type BlockSize = 'S' | 'M' | 'L';

export interface TemplateBlock {
  id: string;
  type: BlockType;
  content: string;
  align?: BlockAlign;
  size?: BlockSize;

  imageUrl?: string;

  url?: string;
}

export interface Template {
  id: string;
  name: string;
  blocks: TemplateBlock[];
  brandColor: string;
  width: number;

  subject?: string;
}

export interface StateConfig {
  stateCode: number;
  stateName: string;

  enabled: boolean;

  templateId?: string;
  templateName: string;

  wspTemplateName?: string;
}

export interface ChannelConfig {
  channel: Channel;
  states: StateConfig[];
}

export interface NotificationConfig {
  ownerType: number;
  ownerId: number;

  isInheriting: boolean;

  inheritsFromOwnerId?: number;
  email: ChannelConfig;
  whatsapp: ChannelConfig;
}

export interface WspMapRow {
  component: 'Header' | 'Cuerpo' | 'Botón (URL · posicional)';
  param: string;
  variable: string;
}

export const INITIAL_STATE_CODES = [2, 3, 4, 8, 20, 31, 32];

export const STATE_CATALOG: Record<number, string> = {
	2:  'Arribado a PaQuery Point',
	3:  'En poder del paquer',
	4:  'En camino',
	8:  'Disponible para retiro',
	20: 'Entregado',
	21: 'Cancelado',
	24: 'Disponible en tienda',
	28: 'Siniestrado',
	31: 'Intento de entrega 1',
	32: 'Intento de entrega 2',
	39: 'En devolución',
	40: 'Devuelto',
};

export const STATE_FLOW_ORDER = [2, 8, 3, 4, 31, 32, 20, 24, 21, 39, 28, 40];

export const compareStatesByFlow = (a: number, b: number): number => {
  const ia = STATE_FLOW_ORDER.indexOf(a);
  const ib = STATE_FLOW_ORDER.indexOf(b);
  return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
};

export const TEMPLATE_VARIABLES = [
  '{nombre_cliente}',
  '{numero_seguimiento}',
  '{direccion_envio}',
  '{fecha_entrega}',
  '{nombre_tienda}',
  '{nombre_marketplace}',
  '{codigo_paquer}',
  '{motivo_visita_1}',
  '{motivo_visita_2}',
  '{codigo_qr}',
];

export const PREVIEW_SAMPLE: Record<string, string> = {
  '{nombre_cliente}': 'Jorge Bravo',
  '{numero_seguimiento}': 'ExternalCodeTest',
  '{direccion_envio}': 'Direccion de prueba, Capital Federal',
  '{fecha_entrega}': '28/08/2026 20:54:03',
  '{nombre_tienda}': 'Tienda de prueba',
  '{nombre_marketplace}': 'Marketplace de prueba',
  '{codigo_paquer}': 'PQ-1042',
  '{motivo_visita_1}': 'Domicilio cerrado',
  '{motivo_visita_2}': 'Nadie atendió el timbre',
  '{codigo_qr}': 'QR-8842-XZ',

  '{foto_visita_1}':
    "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='600' height='240'><rect width='100%' height='100%' fill='%23e5e7eb'/><text x='50%' y='50%' font-family='sans-serif' font-size='20' fill='%239ca3af' text-anchor='middle' dominant-baseline='middle'>Foto de visita 1</text></svg>",
  '{foto_visita_2}':
    "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='600' height='240'><rect width='100%' height='100%' fill='%23e5e7eb'/><text x='50%' y='50%' font-family='sans-serif' font-size='20' fill='%239ca3af' text-anchor='middle' dominant-baseline='middle'>Foto de visita 2</text></svg>",
};

export const WSP_TEMPLATE_BY_STATE: Record<number, string> = {
  2: 'chzk_arrived_at_deposit',
  3: 'chzk_driver_on_way',
  4: 'chzk_driver_inminent_delivery',
  8: 'chzk_available_to_withdraw',
  20: 'chzk_delivery',
  31: 'chzk_delivery_try_1',
  32: 'chzk_delivery_try_1',
};

export const WSP_VARIABLE_MAP: Record<string, WspMapRow[]> = {
  __default: [
    {
      component: 'Header',
      param: 'customer_name',
      variable: '{nombre_cliente}',
    },
    {
      component: 'Cuerpo',
      param: 'tracking_code',
      variable: '{numero_seguimiento}',
    },
    {
      component: 'Cuerpo',
      param: 'shipping_address',
      variable: '{direccion_envio}',
    },
    {
      component: 'Botón (URL · posicional)',
      param: 'url_suffix [0]',
      variable: '{seguimiento_b64}',
    },
  ],
};

export const getWspVariableMap = (templateName: string): WspMapRow[] =>
  WSP_VARIABLE_MAP[templateName] ?? WSP_VARIABLE_MAP.__default;

export const renderWithSample = (
  text: string,
  sample: Record<string, string> = PREVIEW_SAMPLE,
): string => text.replace(/\{[a-z_]+\}/g, (match) => sample[match] ?? match);

const emailBlocks = (stateName: string): TemplateBlock[] => [
  { id: 'header', type: 'header', content: 'logo Chazki' },
  {
    id: 'text',
    type: 'text',
    content: `Hola {nombre_cliente}, tu pedido {numero_seguimiento}: ${stateName.toLowerCase()} en {direccion_envio}.`,
    align: 'center',
    size: 'M',
  },
  { id: 'button', type: 'button', content: 'Seguir envío' },
  { id: 'footer', type: 'footer', content: '{nombre_tienda}' },
];

export const buildDefaultTemplate = (state: StateConfig): Template => ({
  id:
    state.templateId && /^\d+$/.test(state.templateId) ? state.templateId : '',
  name: state.templateName,
  blocks: emailBlocks(state.stateName),
  brandColor: '#1B6FE0',
  width: 600,
});

const getConfigRequest = async (
  ownerType: number,
  ownerId: number,
): Promise<NotificationConfig> => {
  const { data } = await Api.apiAxios.get(
    api.notificationHub.config(ownerType, ownerId),
  );
  return data as NotificationConfig;
};

const putConfigRequest = async (
  config: NotificationConfig,
): Promise<NotificationConfig> => {
  const { data } = await Api.apiAxios.put(
    api.notificationHub.config(config.ownerType, config.ownerId),
    config,
  );
  return data as NotificationConfig;
};

const getTemplateRequest = async (id: string): Promise<Template> => {
  const { data } = await Api.apiAxios.get(api.notificationHub.template(id));
  return data as Template;
};

const createTemplateRequest = async (template: Template): Promise<Template> => {
  const previousId = /^\d+$/.test(template.id) ? template.id : undefined;
  const { data } = await Api.apiAxios.post(api.notificationHub.templates, {
    name: template.name,
    subject: template.subject,
    brandColor: template.brandColor,
    width: template.width,
    blocks: template.blocks,
    previousId,
  });
  return data as Template;
};

export const notificationTemplatesKeys = {
  config: (ownerType: number, ownerId: number) =>
    ['notificationTemplates', 'config', ownerType, ownerId] as const,
  template: (id: string) => ['notificationTemplates', 'template', id] as const,
};

export const useNotificationConfig = (
  ownerType?: number,
  ownerId?: number,
  enabled = true,
) =>
  useQuery({
    queryKey: notificationTemplatesKeys.config(ownerType ?? 0, ownerId ?? 0),
    queryFn: () => getConfigRequest(ownerType as number, ownerId as number),
    enabled: enabled && !!ownerType && !!ownerId,
    staleTime: 60000,
  });

export const useTemplate = (id?: string, enabled = true) =>
  useQuery({
    queryKey: notificationTemplatesKeys.template(id ?? ''),
    queryFn: () => getTemplateRequest(id as string),
    enabled: enabled && !!id,
    staleTime: 60000,
  });

export const useCreateTemplate = () =>
  useMutation({ mutationFn: createTemplateRequest });

export const useSaveNotificationConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: putConfigRequest,
    onSuccess: (saved) => {
      queryClient.setQueryData(
        notificationTemplatesKeys.config(saved.ownerType, saved.ownerId),
        saved,
      );
    },
  });
};

export const getAddableStates = (
  config: NotificationConfig | undefined,
  channel: Channel,
): Array<{ stateCode: number; stateName: string }> => {
  if (!config) return [];
  const present = new Set(config[channel].states.map((s) => s.stateCode));
  return Object.entries(STATE_CATALOG)
    .map(([code, name]) => ({ stateCode: Number(code), stateName: name }))
    .filter((s) => !present.has(s.stateCode));
};
